import { useCallback, useState, type ReactNode } from 'react'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { updateTask } from '@/features/preinvest/tasks/api'
import { ContactRhythmPopover, type ContactType } from '@/features/tasks/ContactRhythmPopover'
import { runWithFeedback } from '@/lib/feedback'
import { completeOccurrence, postponeOccurrence } from '../agreements/api'
import { ActivityComposer, type ComposerRelation, type ComposerType } from '../timeline/ActivityComposer'
import { addDays } from './dates'
import { itemKey } from './itemKey'
import type { DashboardItem } from './types'

export type Postpone = { days: 7 | 14 } | { date: string }

export interface ItemActions {
  busy: ReadonlySet<string>
  /** Ticks an item: a contact-rhythm task asks "Hoe had je contact?" first. */
  complete: (item: DashboardItem) => void
  postpone: (item: DashboardItem, change: Postpone) => void
  /** Wraps `children` (the checkbox or button) in the "Hoe had je contact?" popover for a rhythm task. */
  rhythmAnchor: (item: DashboardItem, children: ReactNode) => ReactNode
  /** Render once on the page: the sheet where a contact moment is logged. */
  dialogs: ReactNode
}

function composerRelation(item: DashboardItem): ComposerRelation | null {
  const relation = item.relation
  if (!relation) return null
  return { kind: relation.type, id: relation.id, name: relation.name }
}

/**
 * What ticking, postponing and logging do for the lines of the Overzicht (design §4.1, §4.10): a task is
 * ticked through the task endpoint, a postponed agreement task moves its occurrence, a deadline without
 * task is completed as an occurrence. After a change `onChanged` refetches the affected widgets.
 */
export function useItemActions(today: string, onChanged: () => void): ItemActions {
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set())
  const [rhythmKey, setRhythmKey] = useState<string | null>(null)
  const [logging, setLogging] = useState<{ item: DashboardItem; type: ComposerType } | null>(null)

  const run = useCallback(
    async (item: DashboardItem, action: () => Promise<unknown>, error: string) => {
      const key = itemKey(item)
      setBusy((current) => new Set(current).add(key))
      const result = await runWithFeedback(action, { error })
      setBusy((current) => {
        const next = new Set(current)
        next.delete(key)
        return next
      })
      if (result !== undefined) onChanged()
    },
    [onChanged],
  )

  const tick = useCallback(
    (item: DashboardItem) => {
      if (item.task_id) {
        const id = item.task_id
        void run(item, () => updateTask(id, { status: 'done' }), 'Taak afvinken mislukt.')
      } else if (item.occurrence_id) {
        const id = item.occurrence_id
        void run(item, () => completeOccurrence(id), 'Afhandelen mislukt.')
      }
    },
    [run],
  )

  const complete = useCallback(
    (item: DashboardItem) => {
      if (item.task_id && item.schedule === 'contactritme') {
        setRhythmKey(itemKey(item))
        return
      }
      tick(item)
    },
    [tick],
  )

  const postpone = useCallback(
    (item: DashboardItem, change: Postpone) => {
      if (item.occurrence_id) {
        const id = item.occurrence_id
        const body = 'days' in change ? { days: change.days } : { due_date: change.date }
        void run(item, () => postponeOccurrence(id, body), 'Uitstellen mislukt.')
      } else if (item.task_id) {
        const id = item.task_id
        const due = 'days' in change ? addDays(today, change.days) : change.date
        void run(item, () => updateTask(id, { due_date: due }), 'Uitstellen mislukt.')
      }
    },
    [run, today],
  )

  const rhythmAnchor = useCallback(
    (item: DashboardItem, children: ReactNode): ReactNode => {
      if (!(item.task_id && item.schedule === 'contactritme')) return children
      const key = itemKey(item)
      return (
        <ContactRhythmPopover
          open={rhythmKey === key}
          onOpenChange={(open) => {
            if (!open) setRhythmKey(null)
          }}
          anchor={<span>{children}</span>}
          onLog={(type: ContactType) => {
            setRhythmKey(null)
            if (composerRelation(item)) setLogging({ item, type })
            else tick(item) // no relation to log on: just tick it off
          }}
          onTickOnly={() => {
            setRhythmKey(null)
            tick(item)
          }}
        />
      )
    },
    [rhythmKey, tick],
  )

  const relation = logging ? composerRelation(logging.item) : null
  const dialogs = (
    <Sheet open={logging !== null && relation !== null} onOpenChange={(open) => (open ? undefined : setLogging(null))}>
      <SheetContent side="right" size="md" aria-describedby={undefined}>
        <SheetTitle className="text-lg">{`Contact vastleggen met ${relation?.name ?? ''}`}</SheetTitle>
        {relation && logging ? (
          <ActivityComposer
            relation={relation}
            open
            type={logging.type}
            autoFocus
            onOpenChange={(open) => (open ? undefined : setLogging(null))}
            onTypeChange={(type) => setLogging({ item: logging.item, type })}
            onSaved={() => {
              setLogging(null)
              onChanged()
            }}
            onTaskCreated={onChanged}
          />
        ) : null}
      </SheetContent>
    </Sheet>
  )

  return { busy, complete, postpone, rhythmAnchor, dialogs }
}
